// Shape guards for client-supplied text that is about to reach Postgres, so a
// malformed value is answered as a 400/404 instead of a cast or encoding 500.
// Pure and client-safe: the compose schemas run it inside RHF too (ADR 016).

/** The 36-char `8-4-4-4-12` hex shape every uuid column (and `gen_random_uuid()`) uses. */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The shape Postgres prints a `timestamptz` in, and the ISO form a client may echo back. */
const TIMESTAMPTZ_TEXT = /^(\d{4})-(\d{2})-(\d{2})[T ]\d{2}:\d{2}:\d{2}(\.\d{1,6})?(Z|[+-](\d{2})(:?\d{2})?)$/;

/** The widest UTC offset Postgres casts: ±15:59 (BL-2481). JS parses up to ±23:59. */
const MAX_OFFSET_HOURS = 15;

/** Every NUL — the one character Postgres refuses in any `text` value (SQLSTATE 22021). */
const NUL = /\u0000/g;

/**
 * Does the text hold at least one character a reader can see? (BUG-2253, BUG-2671.) JS `trim()` keeps
 * zero-width characters (U+200B-U+200D, U+2060, U+FEFF and the rest of `\p{Cf}`), so a required
 * text made only of those saved as a blank-looking value. The one rule every required free-text field
 * (a note, a reason) shares. Visible text that also holds a joiner (an emoji sequence), RTL marks or
 * HTML passes: one visible character is enough, and nothing is stripped.
 *
 * @param text - the text as typed.
 * @returns `true` when some character is neither whitespace nor a format character.
 */
export function hasVisibleText(text: string): boolean {
    return /[^\s\p{Cf}]/u.test(text);
}

/**
 * Whether `value` is shaped like a UUID. A shape check, not RFC 4122: it does
 * not look at version bits, so it never refuses an id Postgres would accept.
 *
 * @param value - the candidate id.
 * @returns true when a uuid column can be compared against it without a cast error.
 */
export function isUuid(value: string): boolean {
    return UUID_SHAPE.test(value);
}

/**
 * Whether `text` is a timestamp Postgres will cast with `::timestamptz`.
 *
 * Strict on purpose: `Date.parse` tolerates free text Postgres refuses, and
 * both would roll a Feb 30 over to March, so the shape is matched and the day
 * checked against the month.
 *
 * @param text - the candidate timestamp.
 * @returns true when the cast cannot fail.
 */
export function isTimestamptzText(text: string): boolean {
    const m = TIMESTAMPTZ_TEXT.exec(text);
    if (!m || Number.isNaN(Date.parse(text))) return false;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    // Postgres has no year 0 (22008) and no offset past ±15:59 (22009); JS accepts both.
    if (y < 1 || (m[6] != null && Number(m[6]) > MAX_OFFSET_HOURS)) return false;
    return mo >= 1 && mo <= 12 && d >= 1 && d <= new Date(Date.UTC(y, mo, 0)).getUTCDate();
}

/**
 * Remove every NUL character. Stripped rather than refused: it is invisible,
 * never typed on purpose, and the rest of the text is what the sender meant.
 *
 * @param value - user-entered text.
 * @returns the text with no `\u0000` left in it.
 */
export function stripNulCharacters(value: string): string {
    return value.replace(NUL, '');
}

/**
 * Every NUL, and every UTF-16 surrogate that is not half of a pair. Postgres refuses a NUL in any
 * `text`/`jsonb` value (22021) and a lone surrogate in `jsonb` (the escape `\ud800` has no pair), so a
 * body carrying either fails mid-write. Valid pairs (emoji) never match.
 */
const UNSTORABLE_TEXT = /\u0000|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;

/**
 * Walk an already-parsed JSON body and name every string a Postgres text or jsonb value cannot hold
 * (BUG-2345). The one rule behind the shared body parser: refused up front as a field error, not left to
 * fail the write (and its Activity entry) as a 500. Text that stores as typed (emoji, RTL, combining
 * marks, HTML) passes untouched.
 *
 * @param raw - the parsed request body (any JSON value).
 * @returns dotted paths of the offending strings in document order; `''` is a bare string body; `[]` when clean.
 */
export function findUnstorableTextPaths(raw: unknown): string[] {
    const found: string[] = [];
    const seen = new WeakSet<object>();
    // One shared stack, joined only on a hit: the walk runs on every parsed body, so it allocates nothing per leaf.
    const path: string[] = [];
    const visit = (value: unknown): void => {
        if (typeof value === 'string') {
            if (UNSTORABLE_TEXT.test(value)) found.push(path.join('.'));
            return;
        }
        if (value === null || typeof value !== 'object' || seen.has(value)) return;
        seen.add(value);
        for (const [key, child] of Object.entries(value)) {
            // A record key lands in jsonb too; it has no field of its own, so the error names its parent.
            if (UNSTORABLE_TEXT.test(key)) found.push(path.join('.'));
            path.push(key);
            visit(child);
            path.pop();
        }
    };
    visit(raw);
    return found;
}
